import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { _className, LL_H_C, LL_V_L, openContent, ProtoComponent, TS_Input, VirtualizedList } from '@nu-art/thunderstorm-frontend/index';
import { compare, exists, flatArray, MUSTNeverHappenException, sortArray } from '@nu-art/ts-common';
import './Component_SearchAndResults.scss';
import { ModuleFE_SearchV2 } from '../../modules/search/index.js';
import { ICONS } from '../../../icons/index.js';
import { ActionsMenu_BaseManagerUI } from '../../ui-manager/headers/ActionsMenu/ActionsMenu_BaseManagerUI.js';
import { sortType_Ascending, sortType_Descending, sortType_RecentlyAdded, storageKey_SearchAndResult_SelectedSort } from './consts.js';
export class Component_SearchAndResults extends ProtoComponent {
    // ######################### Properties #########################
    static defaultProps = {
        keys: ['searchGroup', 'searchTerm', 'searchItem']
    };
    debounceTimeout;
    // ######################### Life Cycle #########################
    constructor(p) {
        super(p);
        //Start listening on relevant modules
        this.props.searchItems.forEach(searchItem => {
            const module = searchItem.module;
            //@ts-ignore
            this[module.defaultDispatcher.method] = (...params) => {
                this.onSearch(this.state.searchTerm, true);
            };
        });
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        //Stop listening on old modules
        this.props.searchItems.forEach(searchItem => {
            const module = searchItem.module;
            // @ts-ignore
            delete this[module.defaultDispatcher.method];
        });
        super.UNSAFE_componentWillReceiveProps(nextProps);
        //Start listening on relevant modules
        nextProps.searchItems.forEach(searchItem => {
            const module = searchItem.module;
            // @ts-ignore
            this[module.defaultDispatcher.method] = async (...params) => {
                this.onSearch(this.state.searchTerm, true);
            };
        });
    }
    deriveStateFromProps(nextProps, _state) {
        _state = super.deriveStateFromProps(nextProps, _state);
        _state.results ??= [];
        _state.listContainerRef ??= React.createRef();
        _state.selectedItem = nextProps.selectedItem;
        _state.selectedSort = storageKey_SearchAndResult_SelectedSort.get(sortType_Ascending);
        //Get initial search term from url param
        _state.searchTerm ??= this.getQueryParam('searchTerm', '');
        //If showing single search item, set it as selection in filter
        if (nextProps.searchItems.length === 1) {
            _state.searchFilters = {
                searchItemKey: nextProps.searchItems[0].type,
                selectedSearchGroup: nextProps.searchItems[0].groupId[0],
            };
        }
        else { //Not showing a single search item, get the selected group and item from url params
            _state.searchFilters = {
                selectedSearchGroup: this.getQueryParam('searchGroup', ''),
                searchItemKey: this.getQueryParam('searchItem', ''),
            };
            if (_state.selectedItem && !_state.searchFilters.selectedSearchGroup) {
                const searchItem = nextProps.searchItems.find(searchItem => searchItem.type === _state.selectedItem?.type);
                if (!searchItem)
                    throw new MUSTNeverHappenException(`search item for type ${_state.selectedItem.type} not found`);
                _state.searchFilters.selectedSearchGroup = searchItem.groupId[0];
            }
        }
        //Take into account changes to the filters that might occur out of this component, and re-calculate results
        if (this.state?.searchFilters && !compare(this.state.searchFilters, _state.searchFilters))
            this.onSearch(_state.searchTerm);
        return _state;
    }
    componentDidMount() {
        this.onSearch(this.state.searchTerm, true);
        this.onFiltersUpdated();
    }
    // ######################### Logic #########################
    onSearch = (searchTerm, immediate = false) => {
        this.setState({ searchTerm }, () => {
            clearTimeout(this.debounceTimeout);
            if (immediate)
                return this.onSearchImpl();
            this.debounceTimeout = setTimeout(() => this.onSearchImpl(), 500);
        });
    };
    onSearchImpl = async () => {
        const searchTerm = this.state.searchTerm;
        if (!exists(searchTerm) || (searchTerm.length >= 1 && searchTerm.length < 3))
            return [];
        const start = performance.now();
        // filter on which module we perform the search on
        const selectedSearchItem = this.props.searchItems.find(i => i.type === this.state.searchFilters.searchItemKey);
        const toSearchIn = selectedSearchItem ? [selectedSearchItem] : this.props.searchItems;
        ModuleFE_SearchV2.query(toSearchIn, searchTerm)
            .then((results) => {
            const sorted = results.map(group => ({
                type: group.type,
                results: this.sortResults(group),
            }));
            this.setState({ results: sorted });
        }).catch((err) => {
            this.logError(err);
            this.setState({ err });
        });
        const elapsed = performance.now() - start;
        this.setState({ searchTime: (elapsed !== 0 && searchTerm.length) ? elapsed : undefined });
    };
    sortResults = (resultGroup) => {
        const selectedSort = this.state.selectedSort;
        const searchItem = this.props.searchItems.find(item => item.type === resultGroup.type);
        if (!searchItem)
            return resultGroup.results;
        return sortArray(resultGroup.results, item => {
            if (selectedSort.objectSortingProp && selectedSort.objectSortingProp.some(_prop => !!item[_prop])) {
                const foundKey = selectedSort.objectSortingProp.find(_prop => !!item[_prop]);
                if (typeof item[foundKey] === 'string')
                    return item[foundKey].trim().toLowerCase();
                return item[foundKey];
            }
            const mapperValues = searchItem.mapper(item).map(_item => _item.toLowerCase());
            //if mapper returns one value return it
            if (mapperValues.length === 1)
                return mapperValues[0];
            // if not use the same sort logic to return the strongest value in the string array (the one fitting the most of what we look for)
            return sortArray(mapperValues, i => i, selectedSort.invert);
        });
    };
    getResultAmount = () => {
        return this.state.results.reduce((acc, curr) => {
            return acc + curr.results.length;
        }, 0);
    };
    onSearchClear = () => {
        this.setState({
            searchTerm: '',
            results: []
        }, () => {
            this.onFiltersUpdated();
            this.onSearch('', true);
        });
    };
    onSearchResultClick = (resultId, resultKey) => {
        this.props.onSearchItemSelected({ type: resultKey, id: resultId });
    };
    onFiltersUpdated = () => {
        const query = {};
        query.searchTerm = this.state.searchTerm ? this.state.searchTerm : undefined;
        query.searchGroup = this.state.searchFilters.selectedSearchGroup ? this.state.searchFilters.selectedSearchGroup : undefined;
        query.searchItem = this.state.searchFilters.searchItemKey ? this.state.searchFilters.searchItemKey : undefined;
        this.setQueryParams(query);
    };
    updateSelectedSort = (selectedSort) => {
        this.setState({ selectedSort: selectedSort }, () => {
            storageKey_SearchAndResult_SelectedSort.set(selectedSort);
        });
    };
    // ######################### Render - Search #########################
    renderClear() {
        return '';
        // return <span onClick={() => this.onSearch('')}>CLEAR</span>;
    }
    renderSearchTimer = () => {
        return _jsx("span", { className: 'search-timer', children: this.state.searchTime ? `${this.state.searchTime.toFixed(3)} ms` : ' ' });
    };
    renderActionMenu = () => {
        if (!this.state.results.length)
            return '';
        const sortMenuItems = [
            {
                label: this.renderActionMenuTitle(sortType_RecentlyAdded.label),
                action: () => this.updateSelectedSort(sortType_RecentlyAdded)
            },
            {
                label: this.renderActionMenuTitle(sortType_Ascending.label),
                action: () => this.updateSelectedSort(sortType_Ascending)
            },
            {
                label: this.renderActionMenuTitle(sortType_Descending.label),
                action: () => this.updateSelectedSort(sortType_Descending)
            }
        ];
        return _jsxs(LL_H_C, { ...openContent.popUp.bottom('search-and-results__action-list', (refresh) => _jsx(ActionsMenu_BaseManagerUI, { actions: sortMenuItems })), className: 'search-and-results__sort-list', children: [_jsx(ICONS.newSort.component, { className: 'sort-icon' }), _jsx("div", { className: 'search-and-results__sort-list-title', children: "Sort List" })] });
    };
    renderActionMenuTitle = (title) => {
        const isSelected = title === this.state.selectedSort.label;
        return _jsxs(LL_H_C, { className: 'match_parent flex__space-between', children: [title, isSelected && _jsx(ICONS.v.component, {})] });
    };
    renderSearchBox() {
        return _jsxs(LL_H_C, { className: 'search-and-results__search', children: [_jsx(TS_Input, { type: 'text', placeholder: 'Search', value: this.state.searchTerm, onChange: value => this.onSearch(value), onBlur: () => this.onFiltersUpdated() }), _jsx(ICONS.search.component, { className: 'search-and-results__search__glass' }), _jsx(ICONS.x.component, { className: 'search-and-results__search__clear', onClick: this.onSearchClear })] });
    }
    renderFilters() {
        if (this.props.searchItems.length === 1)
            return;
        return (_jsxs(LL_V_L, { className: 'search-filters', children: [this.renderClear(), this.renderEntityFilters()] }));
    }
    renderEntityFilters() {
        const groups = this.props.searchItems.reduce((acc, curr) => {
            curr.groupId.forEach(id => {
                if (!acc.includes(id))
                    acc.push(id);
            });
            return acc;
        }, []);
        const searchItems = this.props.searchItems.filter(item => item.groupId.includes(this.state.searchFilters.selectedSearchGroup));
        return _jsxs(LL_V_L, { children: [_jsx(LL_H_C, { className: 'search-filters__by-search-groups', children: groups.map(group => {
                        const className = _className('search-filters__by-search-groups__item', this.state.searchFilters.selectedSearchGroup === group && 'selected');
                        return _jsx("div", { onClick: () => {
                                this.setState({
                                    searchFilters: { selectedSearchGroup: group, searchItemKey: '' },
                                    results: [],
                                }, () => this.onFiltersUpdated());
                            }, className: className, children: group }, group);
                    }) }), _jsx(LL_H_C, { className: 'search-filters__by-search-item', children: searchItems.map(searchItem => {
                        const className = _className('search-filters__by-search-item__item', searchItem.type === this.state.searchFilters.searchItemKey && 'selected');
                        return _jsx("div", { className: className, onClick: () => {
                                this.setState({
                                    searchFilters: {
                                        ...this.state.searchFilters,
                                        searchItemKey: searchItem.type,
                                    }
                                }, () => {
                                    this.onFiltersUpdated();
                                    this.onSearch(this.state.searchTerm);
                                });
                            }, children: searchItem.entityLabel }, searchItem.type);
                    }) })] });
    }
    renderResultCount = () => {
        const resultAmount = this.getResultAmount();
        if (!resultAmount)
            return;
        return _jsxs("div", { className: 'search-results-amount', children: [resultAmount, " Results"] });
    };
    renderSearchAndFilters = () => {
        return _jsxs(LL_V_L, { className: 'search-area', children: [this.renderSearchTimer(), this.renderSearchBox(), this.renderFilters(), _jsxs(LL_H_C, { className: 'match_width search-area__utils-bar', children: [this.renderActionMenu(), this.renderResultCount()] })] });
    };
    // ######################### Render - Results #########################
    renderResults = () => {
        const itemsToRender = flatArray(this.state.results.map(resultGroup => {
            const searchItem = this.props.searchItems.find(item => item.type === resultGroup.type);
            if (!searchItem)
                return [];
            return resultGroup.results.map(result => searchItem.renderer(result, result._id === this.state.selectedItem?.id, () => this.onSearchResultClick(result._id, searchItem.type), this.props.getRightClickActions));
        }));
        return _jsx(_Fragment, { children: _jsx("div", { className: 'search-result-list-container', ref: this.state.listContainerRef, children: _jsx(VirtualizedList, { className: 'search-results', listToRender: itemsToRender, itemHeight: 110, height: this.state.listContainerRef.current?.clientHeight ?? 0 }) }) });
    };
    // ######################### Render #########################
    render() {
        return (_jsxs(LL_V_L, { className: 'search-and-results', children: [this.renderSearchAndFilters(), this.renderResults()] }));
    }
}
//# sourceMappingURL=Component_SearchAndResults.js.map